import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import ReactPlayer from "react-player";
import { useNavigate } from "react-router";
import { mdiAlertCircleOutline, mdiFilterVariant } from "@mdi/js";
import Icon from "@mdi/react";
import dayjs from "dayjs";
import { useToggle } from "usehooks-ts";

import loadEmpty from "assets/images/load-empty.png";
import Button from "components/Button";
import CustomMap from "components/CustomMap";
import DataTable from "components/DataTable";
import Header from "components/Header";
import Modal from "components/Modal";
import SubHeaderWithFilter from "components/SubHeaderWithFilter";
import TipModal from "components/TipModal";
import { LOAD_SEARCH_HELP } from "constants/link";
import {
  LOAD_FILTER_ITEMS,
  LOAD_SEARCH_HEADER_LIST,
  LOAD_SEARCH_EXPANDABLE_HEADER_LIST
} from "constants/loads";
import { routes } from "constants/routes";
import { usePullToRefresh } from "context/PullToRefreshContext";
import { showErrorMessage } from "helpers/toastMessage";
import useDeviceType from "hooks/useDeviceType";
import usePaginatedQuery from "hooks/usePaginatedQuery";
import MainLayout from "layouts/Main";
import LoadingSpinner from "components/LoadingSpinner";
import LoadFilterForm from "../modules/LoadFilterForm";
import LoadPostedItem from "../modules/LoadPostedItem/LoadPostedItem";
import { QUERY_LOAD_SEARCH } from "./query";
import styles from "./style.module.scss";
import { IQueryLoadData } from "models/Load";
import { ITruckData } from "models/Truck";

function MobileLoadList({
  data,
  isInitialLoading,
  onClickDetails
}: Readonly<{
  data: IQueryLoadData[];
  isInitialLoading: boolean;
  onClickDetails: (id: string) => void;
}>) {
  let content: ReactNode;

  if (data.length > 0) {
    content = data.map((item) => (
      <LoadPostedItem key={item.id} data={item} onClickDetails={onClickDetails} editable={false} />
    ));
  } else if (isInitialLoading) {
    content = <LoadingSpinner isFetching={isInitialLoading} />;
  } else {
    content = (
      <div className={styles.loadEmptyList}>
        <img src={loadEmpty} alt="Empty load list" />
        <h3 className={styles.loadEmptyTitle}>No results found.</h3>
        <p className="font-medium text-center">Try refining your search to view results.</p>
      </div>
    );
  }

  return <ul className={styles.loadList}>{content}</ul>;
}

export default function LoadSearch() {
  const { isDesktop } = useDeviceType();
  const [isFilterModalOpen, toggleFilterModal] = useToggle();
  const [isTipModalOpen, toggleTipModal] = useToggle();
  const [viewType, setViewType] = useState("list");
  const [filter, setFilter] = useState<Record<string, any>>({
    orderBy: "-time_posted"
  });
  const [isHideDesktopFilter, setIsHideDesktopFilter] = useState(true);

  const ContainerRef = useRef<HTMLDivElement>(null);
  const [isScroll, setIsScroll] = useState(false);

  const {
    data,
    loading,
    error,
    loadNextPage,
    pageInfo,
    totalCount,
    goToPage,
    loadPreviousPage,
    setPageSize,
    resetPagination,
    isRefetching,
    refresh
  } = usePaginatedQuery<any, any>({
    query: QUERY_LOAD_SEARCH,
    variables: filter,
    cursorField: "loadsSearch",
    pageSize: 20,
    loadMore: true
  });

  const isInitialLoading = loading && data.length === 0 && !isRefetching;
  usePullToRefresh(refresh, { enabled: viewType === "list" && !isInitialLoading });

  useEffect(() => {
    if (filter?.orderBy !== "-time_posted" && isScroll) {
      setIsScroll(false);
    }
  }, [isScroll, filter?.orderBy]);

  useEffect(() => {
    if (!loading && error) {
      showErrorMessage(error.message);
    }
  }, [loading]);

  const mapPoints = useMemo(
    () =>
      data && data.length > 0
        ? data
            .filter(({ originCoords }) => originCoords !== null)
            .map(({ originCoords }) => ({
              lat: originCoords.latitude as number,
              lng: originCoords.longitude as number
            }))
        : [],
    [JSON.stringify(data)]
  );

  //go to details
  const navigate = useNavigate();

  function goDetails(id: string) {
    navigate(`${routes.LOADS_SEARCH}/${id}`);
  }

  const onScroll = () => {
    if (ContainerRef.current) {
      const { scrollTop, offsetHeight, scrollHeight } = ContainerRef.current;
      if (scrollTop + offsetHeight + 1 >= scrollHeight && pageInfo.hasNextPage) {
        setIsScroll(true);
        loadNextPage();
      }
    }
  };

  const goToMoreDetails = (id: string) => {
    navigate(`${routes.LOADS_SEARCH}/${id}`);
  };
  return (
    <MainLayout
      desktopTitle="Load Search"
      customHeader={<Header label="Load Search" goBackPath={routes.LOADS} isHideDesktop />}
      className="pl-3.5 pr-3.5 flex-1 overflow-auto">
      <div className={styles.loadSearch}>
        {isDesktop ? (
          <button type="button" className={styles.proTipButton} onClick={toggleTipModal}>
            <Icon path={mdiAlertCircleOutline} />
            <span className="underline">ProTip</span>
          </button>
        ) : null}

        {isDesktop && isHideDesktopFilter ? (
          <LoadFilterForm
            setFilter={setFilter}
            setIsScroll={setIsScroll}
            toggleFilterModal={toggleFilterModal}
            resetPagination={resetPagination}
          />
        ) : null}

        <SubHeaderWithFilter
          viewType={viewType}
          onViewTypeChange={setViewType}
          filter={filter}
          setFilter={setFilter}
          setIsHideDesktopFilter={setIsHideDesktopFilter}
          filterOptions={LOAD_FILTER_ITEMS}
          toggleTipModal={toggleTipModal}
          filterClass="!w-32"
        />

        <div className={styles.loadSearchBody} ref={ContainerRef} onScroll={onScroll}>
          {viewType === "list" ? (
            <>
              {isDesktop ? (
                <DataTable
                  headers={LOAD_SEARCH_HEADER_LIST}
                  data={data.map((item) => ({
                    ...item,
                    timeStart: {
                      timeStart: item.timeStart,
                      startDay: dayjs(item.timeStart).format("DD MMM"),
                      startTime: dayjs(item.timeStart).format("hh:mm A")
                    },
                    timeEnd: {
                      timeEnd: item.timeEnd,
                      endDay: dayjs(item.timeEnd).format("DD MMM"),
                      endTime: dayjs(item.timeEnd).format("hh:mm A")
                    },
                    timeDelivery: {
                      timeDelivery: item.timeDelivery,
                      deliveryDay: dayjs(item.timeDelivery).format("DD MMM"),
                      deliveryTime: dayjs(item.timeDelivery).format("hh:mm A")
                    },
                    loadId: item.id,
                    originData: {
                      originCity: item.originCity,
                      originState: item.originState
                    },
                    destinationData: {
                      destinationCity: item.destinationCity,
                      destinationState: item.destinationState
                    },
                    details: { action: () => goToMoreDetails(item.id) }
                  }))}
                  sortDisableList={["details", "deadhead"]}
                  totalCount={totalCount}
                  nextLink={pageInfo.hasNextPage}
                  prevLink={pageInfo.hasPreviousPage}
                  setPageSize={setPageSize}
                  goToPage={goToPage}
                  fetchPrevPage={loadPreviousPage}
                  fetchNextPage={loadNextPage}
                  loading={loading}
                  infiniteScroll
                  ExpandableView={({ row }: { row: ITruckData }) => (
                    <DataTable headers={LOAD_SEARCH_EXPANDABLE_HEADER_LIST} data={[row]} simple />
                  )}
                />
              ) : (
                <MobileLoadList
                  data={data}
                  isInitialLoading={isInitialLoading}
                  onClickDetails={goDetails}
                />
              )}
            </>
          ) : (
            <div className={styles.loadMap}>
              <CustomMap center={mapPoints[0]} points={mapPoints} />
            </div>
          )}
        </div>

        {!isDesktop ? (
          <Button
            onClick={toggleFilterModal}
            className={styles.loadSearchRoundButton}
            rounded
            iconButton>
            <Icon path={mdiFilterVariant} className="m-auto" />
          </Button>
        ) : null}

        <Modal
          isOpen={isFilterModalOpen}
          closeModal={toggleFilterModal}
          wrapperClass="!pt-0"
          dialogClass="!bg-foreground !pt-6 !mt-20"
          closeClass="!top-14">
          <LoadFilterForm
            setFilter={setFilter}
            setIsScroll={setIsScroll}
            toggleFilterModal={toggleFilterModal}
            resetPagination={resetPagination}
          />
        </Modal>

        <TipModal
          isModalOpen={isTipModalOpen}
          toggleModal={toggleTipModal}
          title="Load Search"
          content={
            <div className="h-full px-2 overflow-y-auto">
              <div className="text-xs desktop:text-sm">
                <p className="py-2">
                  The default radius for city to city search is 200 miles. Enter a larger radius on
                  either Origin or Destination that you prefer to check loads on lanes against to
                  pull in more matches.
                </p>
                <p className="py-2">
                  Be sure to set the end date on the search out far enough to match up to the window
                  that you’re looking for loads. The default is only set for one day ahead.
                </p>
                <p className="py-2">
                  Check for Return Loads by scrolling to the bottom of any load details page you
                  view to see loads coming up in the future that are returning back on the same lane
                  as well.
                </p>
                <p className="py-2">
                  You can also block users who you don’t want to see in your results by clicking the
                  block user button in the line items or on the load details pages.
                </p>
              </div>

              <ReactPlayer url={LOAD_SEARCH_HELP} playing={false} controls={true} width="100%" />
            </div>
          }
        />
      </div>
    </MainLayout>
  );
}
